import { useCallRecordingUrl } from '@/features/voice-receptionist/api';
import {
  LoaderCircleIcon,
  PauseIcon,
  PlayIcon,
  RotateCcwIcon,
} from 'lucide-react';
import {
  type Ref,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { recordingTimestamp as timestamp } from './call-recording-time';

const PLAYBACK_SPEEDS = [1, 1.25, 1.5, 2];

export interface CallRecordingPlayerHandle {
  seekTo: (seconds: number) => void;
}

/** On-demand signed audio, with the same controls before and during playback. */
export function CallRecordingPlayer({
  callSid,
  durationMs,
  ref,
}: {
  callSid: string;
  durationMs: number | null;
  ref?: Ref<CallRecordingPlayerHandle>;
}) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const { loadRecordingAsync } = useCallRecordingUrl();
  const [playing, setPlaying] = useState(false);
  const [starting, setStarting] = useState(false);
  const [buffering, setBuffering] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speed, setSpeed] = useState(1);
  const speedRef = useRef(1);
  const [error, setError] = useState<'load' | 'blocked' | null>(null);
  const pendingSeek = useRef<number | null>(null);
  const playRequest = useRef<Promise<void> | null>(null);
  const total = duration || Math.max(0, (durationMs ?? 0) / 1000);
  const progress = total > 0 ? Math.min(100, (currentTime / total) * 100) : 0;

  useEffect(() => {
    const audio = audioRef.current;
    return () => {
      if (audio && !audio.paused) audio.pause();
    };
  }, []);

  useImperativeHandle(ref, () => ({
    seekTo(seconds) {
      pendingSeek.current = Math.max(0, seconds);
      if (audioRef.current && error !== 'load')
        applyPendingSeek(audioRef.current);
      void requestPlayback();
    },
  }));

  function applyPendingSeek(audio: HTMLAudioElement) {
    if (
      pendingSeek.current === null ||
      !Number.isFinite(audio.duration) ||
      audio.duration <= 0
    )
      return;
    const time = Math.min(
      pendingSeek.current,
      Math.max(0, audio.duration - 0.01)
    );
    audio.currentTime = time;
    setCurrentTime(time);
    pendingSeek.current = null;
  }

  function requestPlayback() {
    if (!playRequest.current)
      playRequest.current = startPlayback().finally(() => {
        playRequest.current = null;
      });
    return playRequest.current;
  }

  function togglePlayback() {
    const audio = audioRef.current;
    if (audio && !audio.paused && error !== 'load') audio.pause();
    else void requestPlayback();
  }

  async function startPlayback() {
    const audio = audioRef.current;
    if (!audio) return;
    setStarting(true);
    const refresh = error === 'load';
    setError(null);
    try {
      if (!audio.src || refresh) {
        const response = await loadRecordingAsync(callSid);
        if (audioRef.current !== audio) return;
        audio.src = response.url;
      }
      if (audio.ended) audio.currentTime = 0;
      applyPendingSeek(audio);
      audio.playbackRate = speedRef.current;
      await audio.play();
    } catch (cause) {
      // A browser may require another tap after the signed URL was fetched.
      // Keep that source, so the next tap plays without another network wait.
      setError(
        typeof cause === 'object' &&
          cause !== null &&
          'name' in cause &&
          cause.name === 'NotAllowedError'
          ? 'blocked'
          : 'load'
      );
      setPlaying(false);
      setBuffering(false);
    } finally {
      setStarting(false);
    }
  }

  function updateDuration(audio: HTMLAudioElement) {
    if (Number.isFinite(audio.duration) && audio.duration > 0)
      setDuration(audio.duration);
    applyPendingSeek(audio);
  }

  return (
    <div
      className="rounded-lg bg-background p-3"
      role="group"
      aria-label="Call recording player"
    >
      {/* biome-ignore lint/a11y/useMediaCaption: the call transcript is shown above */}
      <audio
        ref={audioRef}
        data-call-recording
        className="hidden"
        preload="none"
        onPlay={(event) => {
          // Only one call recording should speak at a time.
          for (const other of document.querySelectorAll<HTMLAudioElement>(
            'audio[data-call-recording]'
          )) {
            if (other !== event.currentTarget) other.pause();
          }
          setPlaying(true);
          setError(null);
        }}
        onPause={() => setPlaying(false)}
        onPlaying={() => setBuffering(false)}
        onWaiting={() => setBuffering(true)}
        onLoadedMetadata={(event) => updateDuration(event.currentTarget)}
        onDurationChange={(event) => updateDuration(event.currentTarget)}
        onTimeUpdate={(event) =>
          setCurrentTime(event.currentTarget.currentTime)
        }
        onEnded={() => {
          setPlaying(false);
          setBuffering(false);
        }}
        onError={(event) => {
          pendingSeek.current ??= Math.max(
            currentTime,
            event.currentTarget.currentTime
          );
          setPlaying(false);
          setStarting(false);
          setBuffering(false);
          setError('load');
        }}
      />
      <div className="flex items-center gap-3">
        <button
          type="button"
          aria-label={
            starting
              ? 'Loading recording'
              : playing
                ? 'Pause recording'
                : error === 'load'
                  ? 'Retry recording'
                  : 'Play recording'
          }
          disabled={starting}
          onClick={() => void togglePlayback()}
          className="flex size-10 shrink-0 items-center justify-center rounded-full bg-blue-600 text-white transition-colors hover:bg-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-wait motion-reduce:transition-none"
        >
          {starting || (buffering && playing) ? (
            <LoaderCircleIcon
              className="size-4 motion-safe:animate-spin"
              aria-hidden
            />
          ) : playing ? (
            <PauseIcon className="size-4 fill-current" aria-hidden />
          ) : error === 'load' ? (
            <RotateCcwIcon className="size-4" aria-hidden />
          ) : (
            <PlayIcon className="ml-0.5 size-4 fill-current" aria-hidden />
          )}
        </button>
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex items-center justify-between gap-2">
            <span className="text-xs font-medium text-foreground">
              Call recording
            </span>
            <button
              type="button"
              aria-label={`Playback speed ${speed} times`}
              title="Change playback speed"
              onClick={() => {
                const next =
                  PLAYBACK_SPEEDS[
                    (PLAYBACK_SPEEDS.indexOf(speed) + 1) %
                      PLAYBACK_SPEEDS.length
                  ];
                speedRef.current = next;
                if (audioRef.current) audioRef.current.playbackRate = next;
                setSpeed(next);
              }}
              className="rounded-md px-1.5 py-0.5 text-[11px] font-medium tabular-nums text-blue-700 transition-colors hover:bg-blue-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:text-blue-300 dark:hover:bg-blue-900/60"
            >
              {speed}×
            </button>
          </div>
          <div className="flex items-center gap-2.5">
            <div className="relative flex h-5 min-w-0 flex-1 items-center">
              <div
                aria-hidden
                className="pointer-events-none h-1 w-full overflow-hidden rounded-full bg-blue-100 dark:bg-blue-900"
              >
                <div
                  className="h-full rounded-full bg-blue-600 dark:bg-blue-400"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <input
                type="range"
                aria-label="Seek recording"
                aria-valuetext={`${timestamp(currentTime)} of ${timestamp(total)}`}
                min={0}
                max={total || 1}
                step={0.1}
                value={Math.min(currentTime, total)}
                disabled={duration === 0}
                onChange={(event) => {
                  const time = event.currentTarget.valueAsNumber;
                  if (audioRef.current) audioRef.current.currentTime = time;
                  setCurrentTime(time);
                }}
                className="absolute inset-0 m-0 h-full w-full cursor-pointer appearance-none rounded-full bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-default [&::-webkit-slider-thumb]:size-2.5 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-600 [&::-webkit-slider-thumb]:shadow-sm [&::-moz-range-thumb]:size-2.5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-blue-600 dark:[&::-webkit-slider-thumb]:bg-blue-400 dark:[&::-moz-range-thumb]:bg-blue-400"
              />
            </div>
            <span
              aria-hidden
              className="shrink-0 text-[11px] tabular-nums text-muted-foreground"
            >
              {timestamp(currentTime)}{' '}
              <span className="text-muted-foreground/50">/</span>{' '}
              {timestamp(total)}
            </span>
          </div>
        </div>
      </div>
      {error ? (
        <p role="status" className="mt-2 pl-14 text-xs text-muted-foreground">
          {error === 'blocked'
            ? 'Tap play to start the recording.'
            : 'Could not play the recording. Tap to retry.'}
        </p>
      ) : null}
    </div>
  );
}
